//Core
import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'state-manager/store'
import { useHistory } from 'react-router-dom'

// components
import MoneySlider from 'pages/Subscriptions/MoneySlider'
import CustomSubscriptionForm from 'pages/Subscriptions/CustomSubscriptionForm'
import ModalCoupon from 'pages/Subscriptions/ModalCoupon'
import Coupon from 'components/ui/Coupon/Coupon'
import Button from 'components/Onboarding/Button/Button'
import CurrencyToggle from 'components/Onboarding/CurrencyToggle/CurrencyToggle'
import PlanCard from 'components/Onboarding/PlanCard'
import BulletList from 'components/Onboarding/BulletList/BulletList'

// actions
import {
  getAllPlans,
  createPlanRequest,
} from 'state-manager/actions/plans'
import { setCurrentSubscription } from 'state-manager/actions/subscriptions'
import { getUserCoupon } from 'state-manager/actions/coupon'

// hooks
import _useToggle from 'hooks/use-toggle'

// constants
import { PERIOD_MONTH, PERIOD_YEAR } from 'constants/plans'
import {CURRENCIES, ICurrency} from 'constants/currencies'
import { MIN_SLIDER_VALUE, MAX_SLIDER_VALUE } from 'pages/Onboarding/Usage/constants'
import { type } from 'pages/Subscriptions/CustomSubscriptionForm/constants'

//Library
import clsx from 'clsx'

// styles
import classes from 'pages/Onboarding/Usage/Subscriptions/Subscriptions.module.scss'

// Routes
import routes from 'routes'

const Subscriptions = () => {
  const history = useHistory()
  const {
    coupon,
    allPlans,
  } = useAppSelector((state) => ({
    coupon: state.coupon,
    allPlans: state.plans.data
  }))

  const dispatch = useAppDispatch()
  const [isModalOpen, toggleIsModalOpen] = _useToggle()
  const [isCouponModalOpen, toggleCouponModalOpen] = _useToggle()
  const [currency, setCurrency] = useState(CURRENCIES.USD)
  const [selectedPlanType, setSelectedPlanType] = useState(PERIOD_MONTH)
  const [sliderValue, setSliderValue] = useState(MIN_SLIDER_VALUE)

  useEffect(() => {
    if (!allPlans.length) {
      dispatch(getAllPlans())
    }
    dispatch(getUserCoupon()).catch(() => {})
    //eslint-disable-next-line
  }, [])

  if (!allPlans.length) {
    return null
  }

  const monthly = allPlans.filter((item) => item.period === PERIOD_MONTH)
  const yearly = allPlans.filter((item) => item.period === PERIOD_YEAR)

  const monthlyPlan = allPlans.find(
    (item) =>
      item.monthlyMediaSpend === sliderValue &&
      item.currency === currency.id &&
      item.period === PERIOD_MONTH
  )
  const yearlyPlan = allPlans.find(
    (item) =>
      item.monthlyMediaSpend === sliderValue &&
      item.currency === currency.id &&
      item.period === PERIOD_YEAR
  )

  const isLimitReached = sliderValue === MAX_SLIDER_VALUE

  const handleSubscribe = () => {
    if (isLimitReached) {
      toggleIsModalOpen()
      return
    }

    const planId = selectedPlanType === PERIOD_YEAR ? yearlyPlan?.id : monthlyPlan?.id

    if (planId) {
      const formData: {
        planId: number
        token?: string
        coupon?: string
      } = {
        planId: planId,
      }

      if (coupon?.id) {
        formData.coupon = coupon.id
      }
      dispatch(setCurrentSubscription(formData, routes.onboardingConnect))
    }
  }

  const handleCurrencyChange = (currency: ICurrency) => {
    setCurrency(currency)
  }

  const isCouponApplied = Boolean(coupon?.percentOff || coupon?.amountOff)
  const couponMultiplier = coupon?.percentOff ? (100 - coupon?.percentOff) / 100 : 1
  const couponAmount = coupon?.amountOff ? coupon.amountOff / 100 : 0

  // divided by 100 because finalMonthlyPrice is in pennies
  const monthlyPrice = parseInt(`${monthlyPlan ? monthlyPlan.price / 100 : 0}`)
  const yearlyPrice = parseInt(`${yearlyPlan ? yearlyPlan.price / 100 : 0}`)

  const monthlyPriceWithDiscount = coupon?.percentOff
      ? parseInt(`${monthlyPrice * couponMultiplier}`)
      : Math.max(0, parseInt(`${monthlyPrice - couponAmount}`))

  const yearlyPriceWithDiscount = coupon?.percentOff
      ? parseInt(`${yearlyPrice * couponMultiplier}`)
      : Math.max(0, parseInt(`${yearlyPrice - couponAmount}`))

  const yearlyProfit = parseFloat(
    `${
      isCouponApplied
        ? monthlyPrice * 12 - yearlyPriceWithDiscount
        : monthlyPrice * 12 - yearlyPrice
    }`
  )

  const yearlyPriceByMonth = parseInt(`${yearlyPrice / 12}`)
  const yearlyPriceWithDiscountByMonth = parseInt(
    `${yearlyPriceWithDiscount / 12}`
  )

  const handlePlanSelect = (planType: string) => {
    setSelectedPlanType(planType)

    if (isLimitReached) {
      toggleIsModalOpen()
    }
  }

  return (
    <div>
      <div className={'d-flex justify-content-center'}>
        <div className="container text-center">
          <h1>Choose Your Plan</h1>
          <p
            className={clsx(
              classes.descriptionMessage,
              'color-black m-auto'
            )}>
            Move the slider to the monthly spend level you’re closest to.
            This will ensure you’re on the right plan if you choose to continue using EDEE after your free trial.
          </p>
          <div
            className={'d-flex align-items-center mt-2 flex-column-sm-up justify-content-between'}>
            <div className={clsx(classes.slider, 'mb-sm-up-8')}>
              <MoneySlider
                value={sliderValue}
                handleSliderChange={setSliderValue}
                currency={currency.symbol}
                handleStyle={{
                  marginTop: '-8px',
                  width: '27px',
                  height: '27px',
                  borderWidth: '2px',
                  borderColor: '#3DB14A',
                  borderRadius: 13.5,
                  backgroundColor: 'white',
                  padding: 0,
                  boxShadow: '0 1px 1px rgba(40,47,82,0.5), inset 0 0 0 4px white',
                }}
                trackStyle={{
                  backgroundColor: '#3DB14A',
                  height: '13px'
                }}
                railStyle={{
                  backgroundColor: '#F5F5F5',
                  height: '13px'
                }}
                showSpendText={false}
              />
            </div>
            <CurrencyToggle value={currency} onChange={handleCurrencyChange}/>
          </div>
          <div>
            {sliderValue && (
              <div
                className={clsx(classes.planCardContainer)}>
                {Boolean(monthly.length) && (
                  <div className={clsx(classes.planCard)}>
                    <PlanCard
                      isCouponApplied={isCouponApplied}
                      title="Monthly"
                      currency={currency}
                      isLimitReached={isLimitReached}
                      yearlyPrice={yearlyPrice}
                      yearlyPriceWithDiscount={yearlyPriceWithDiscount}
                      monthlyPrice={monthlyPrice}
                      monthlyPriceWithDiscount={monthlyPriceWithDiscount}
                      yearlyProfit={yearlyProfit}
                      yearlyPriceByMonth={yearlyPriceByMonth}
                      yearlyPriceWithDiscountByMonth={yearlyPriceWithDiscountByMonth}
                      isSelected={selectedPlanType === PERIOD_MONTH}
                      onClick={() => handlePlanSelect(PERIOD_MONTH)}
                    />
                  </div>
                )}
                {Boolean(yearly.length) && (
                  <div className={clsx(classes.planCard)}>
                    <PlanCard
                      isCouponApplied={isCouponApplied}
                      title="Yearly"
                      isYearlyCard={true}
                      currency={currency}
                      isLimitReached={isLimitReached}
                      yearlyPrice={yearlyPrice}
                      yearlyPriceWithDiscount={yearlyPriceWithDiscount}
                      monthlyPrice={monthlyPrice}
                      monthlyPriceWithDiscount={monthlyPriceWithDiscount}
                      yearlyProfit={yearlyProfit}
                      yearlyPriceByMonth={yearlyPriceByMonth}
                      yearlyPriceWithDiscountByMonth={yearlyPriceWithDiscountByMonth}
                      isSelected={selectedPlanType === PERIOD_YEAR}
                      onClick={() => handlePlanSelect(PERIOD_YEAR)}
                    />
                  </div>
                )}
              </div>
            )}
            <div className={clsx(classes.featuresCardContainer)}>
              <div className={clsx(classes.featuresCard)}>
                <BulletList
                  features={[
                    'Budget Pacing Reporting',
                    'Automated Budget Management',
                  ]}
                />
              </div>
              <div className={clsx(classes.featuresCard)}>
                <BulletList
                  features={[
                    'Unlimited Ad Accounts & Users',
                    'Email & In-App Alerts',
                  ]}
                />
              </div>
            </div>
          </div>
          <Coupon
            onClick={toggleCouponModalOpen}
            currency={currency.code}
            monthlyPrice={monthlyPrice}
            monthlyPriceWithDiscount={monthlyPriceWithDiscount}
          />
          <div className="d-flex justify-content-center mt-2">
            <Button
              inverted
              className="mr-1"
              dataCy="back"
              title="Back"
              onClick={() => {
                history.push(routes.onboardingCompanyDetails)
              }}
            />
            <Button
              dataCy="continue"
              title="Continue"
              onClick={() => {
                handleSubscribe()
              }}
            />
          </div>
          <div className='d-flex justify-content-center mt-2 mb-2'>
            <p className="text-italic">
              PRICES SHOWN EXCLUDE UK VAT WHICH WILL BE ADDED AT THE PREVAILING RATE ONLY FOR CUSTOMERS IN THE UNITED
              KINGDOM
            </p>
          </div>
        </div>
      </div>
      {isCouponModalOpen && <ModalCoupon onClose={toggleCouponModalOpen}/>}
      {isModalOpen && (
        <CustomSubscriptionForm
          currentSubscriptionType={selectedPlanType === PERIOD_MONTH ? type.monthly : type.yearly}
          handlePlanRequest={(data) => {
            dispatch(createPlanRequest(data)).then(toggleIsModalOpen)
          }}
          onClose={toggleIsModalOpen}
        />
      )}
    </div>
  )
}
export default Subscriptions
